import React from "react";
import shortid from "shortid";
import { Link, Navigate } from "react-router-dom";
import { SignUpInput, CompanyView, Country, Branch, Department, Gender, TimeZoneInfoView } from "../../../models/general";
import { addUpdateBranches, addUpdateDepartments, getBranches, getCompanies, getDepartments } from "../../../api/General/Company";
import { getCountries, getTimeZones } from "../../../api/General/Country";
import { pickTimezoneForCountry } from "../../../utils/countryTimezones";
import { addUsers, getRoles } from "../../../api/General/account";
import { withTranslation } from "react-i18next";
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { connect } from "react-redux";
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { DatePicker as CustomDatePicker } from '../../../components/dateTimePickers';
import { DateField } from '../../../components/dateFields';
import { getTemplates } from '../../../api/Employee/lifecycleTemplates';
import { getEmployeeSelections, startOnboarding } from '../../../api/Employee/onboardingProcesses';
import type { LifecycleTemplateView, EmployeeSelectionView } from '../../../models/employee';
import RestrictedWriteNotice from '../../../components/subscription/RestrictedWriteNotice';
import { RESTRICTED_WRITE_REASON_ID } from '../../../hooks/useWriteAccess';
import { withWriteAccess, type WithWriteAccessProps } from '../../../components/subscription/withWriteAccess';
import { TemplateCategory } from '../../../models/employee';
import PlanCapUpgradeNotice, { PlanCapDetails } from './PlanCapUpgradeNotice';
import { findApiError } from '../../../utils/requestHandling';

/**
 * CO-226. The server refusal code for "this plan does not include any more employees".
 * Deliberately NOT `PLAN_SEAT_CAP_EXCEEDED`: that code exists on the billing surfaces and is
 * rendered there as "reduce headcount or stay on your current plan", which is the wrong advice on
 * a screen whose whole purpose is adding a person.
 */
const PLAN_EMPLOYEE_CAP_REACHED = 'PLAN_EMPLOYEE_CAP_REACHED';

/**
 * CO-114 + E12 — the FREE-tier headcount refusal.
 *
 * A DISTINCT code from the paid ceiling above, on purpose: the two caps are different mechanisms
 * with different routes forward, and `co111-paid-seats-usable.spec.ts` asserts a paid tenant never
 * sees the Free wording. Both are rendered by the same notice component because the SHAPE of the
 * state is identical ("writing is paused, here is the route"); only the sentence differs, and that
 * comes from the server's `details.messageKey`.
 */
const FREE_TIER_EMPLOYEE_CAP_REACHED = 'FREE_TIER_EMPLOYEE_CAP_REACHED';


const days = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
]

interface IUserInput extends SignUpInput {
  weekends: number[];
  id: string;
  // company?: CompanyView;
  country?: Country;
}

interface IState {
  item: IUserInput;
  companies: CompanyView[];
  lastSelectedCompany?: CompanyView;
  countries: Country[];
  lastSelectedCountry?: Country;
  dialogVisible: boolean;
  roles: string[];
  branches: Branch[];
  departments: Department[];
  errorMessage: string;
  success: boolean;
  timeZones: TimeZoneInfoView[];
  onboardingTemplates: LifecycleTemplateView[];
  selectedOnboardingTemplateId: string;
  onboardingStartDate: string;
  onboardingManagerId: string;
  managers: EmployeeSelectionView[];
  isSubmitting: boolean;
  /**
   * The employee WAS created, but a follow-up step (starting the onboarding journey) did not
   * complete.
   *
   * ⚠️ THIS IS NOT COSMETIC. `addUsers` commits the user server-side — the row exists and a
   * BILLABLE SEAT HAS BEEN CONSUMED — before onboarding is ever attempted. Previously any
   * onboarding failure painted the generic "Failed to add users." banner and left the form
   * armed, so the operator read it as "nothing happened" and pressed Submit again, creating a
   * duplicate employee and a second seat. The banner must therefore say what actually
   * happened, and the form must NOT offer a retry that would duplicate the person.
   *
   * The right long-term fix is server-side atomicity (create + onboarding in one transaction,
   * or a compensating rollback). Until that exists, this page's job is simply not to lie.
   */
  partialSuccess: boolean;
  /** The onboarding-specific failure text, shown alongside the "employee was created" message. */
  onboardingErrorMessage: string;
  /**
   * CO-226 — the server's `PLAN_EMPLOYEE_CAP_REACHED` sentence, when THAT is why the add was
   * refused. Held apart from `errorMessage` because it is not a failure: the request was
   * understood, nothing is broken, and the operator has a one-click route forward. Rendering it in
   * the destructive banner told them the opposite three times over.
   *
   * Keyed off `errors[0].code`, never off the text. The description is a human sentence that will
   * be reworded; the code is the contract.
   */
  planCapMessage: string;
  /**
   * The structured half of the same refusal (`errors[0].details`): a stable message key plus the
   * plan name in each locale, the ceiling and the current headcount. The notice renders THIS when
   * it is present and falls back to `planCapMessage` (English) when it is not — an older backend
   * inside the rollback window sends only the sentence.
   */
  planCapDetails: PlanCapDetails | null;
}

interface IProps {
  history: any,
  t: any,
  userId?: any,
  companyName?: any,
  company?: any,
  role?: any,
  companyPrimaryColor?: any
}

// CO-24 — /api/Account/AddUsers is deliberately NOT on the RestrictedWriteAllowlist (the bare
// /api/Account prefix used to be, which handed a non-paying tenant unlimited user creation). The
// submit therefore 403s while Restricted/Dormant, so it must be disabled with a stated reason.
type IPropsWithWrite = IProps & WithWriteAccessProps;


class AddUser extends React.Component<IPropsWithWrite, IState> {

  constructor(props: IProps) {
    super(props);
    this.state = {
      dialogVisible: false,
      item:
      {
        companyId: 0,
        id: shortid.generate(),
        employeeId: "",
        email: "",
        password: `${shortid.generate()}@Abc123`,
        countryId: 0,
        role: "",
        branchId: 0,
        departmentId: 0,
        title: "",
        firstName: "",
        lastName: "",
        phoneNumber: "",
        weekends: [],
        //workingHours: 9,
        //startTime: "",
        //endTime: "",
        //weekendsDelimited: "",
        linkedInUrl: "",
        birthDate: new Date(),
        gender: Gender.Male,
        timeZone: "",
        sendWelcomeEmail: true
      },
      companies: [],
      countries: [],
      roles: [],
      branches: [],
      departments: [],
      errorMessage: "",
      success: false,
      timeZones: [],
      onboardingTemplates: [],
      selectedOnboardingTemplateId: '',
      onboardingStartDate: '',
      onboardingManagerId: '',
      managers: [],
      isSubmitting: false,
      partialSuccess: false,
      onboardingErrorMessage: "",
      planCapMessage: "",
      planCapDetails: null,
    };
  }

  componentDidMount() {
    this.fetch();

  }

  onFormatDate = (date?: Date): string => {
    return !date ? '' : !date ? '' : (date.getFullYear()) + '-' + (date.getMonth() + 1) + '-' + date.getDate();
  };

  fetch = async () => {
    // No company picker: employees are always created in the caller's own company. Operator has no
    // company employee-data access (CLAUDE.md Role Model) and the API now rejects it.
    await this.fetchCountries();
    await this.fetchRoles();
    await this.fetchBranchesAndDepartments();
    await this.fetchTimeZones();
    await this.fetchOnboardingTemplates();
    await this.fetchManagerSelections();
  }

  /* Utility: identify which required fields are missing for UX feedback */
  getMissingRequired = () => {
    const { item, selectedOnboardingTemplateId, onboardingStartDate, onboardingManagerId } = this.state;
    const missing: string[] = [];
    if (!item.firstName) missing.push('firstName');
    if (!item.lastName) missing.push('lastName');
    if (!item.email) missing.push('email');
    if (!item.countryId) missing.push('countryId');
    if (!item.role) missing.push('role');
    if (!item.timeZone) missing.push('timeZone');
    if (item.gender === undefined || item.gender === null) missing.push('gender');
    if (selectedOnboardingTemplateId) {
      if (!onboardingStartDate) missing.push('onboardingStartDate');
      if (!onboardingManagerId) missing.push('onboardingManagerId');
    }
    return missing;
  }

  fetchRoles = async () => {
    const { payload } = await getRoles();
    this.setState({ roles: payload });
  }

  fetchCountries = async () => {
    const { payload } = await getCountries();
    this.setState({ countries: payload });
  };
  fetchTimeZones = async () => {
    const timeZones = await getTimeZones();
    //console.log(timeZones.payload)
    this.setState({ timeZones: timeZones.payload });
  };

  fetchOnboardingTemplates = async () => {
    const result = await getTemplates(TemplateCategory.Onboarding);
    if (!result.errors || result.errors.length === 0) {
      this.setState({ onboardingTemplates: result.payload || [] });
    }
  }

  fetchManagerSelections = async () => {
    const result = await getEmployeeSelections();
    if (!result.errors || result.errors.length === 0) {
      this.setState({ managers: result.payload || [] });
    }
  }

  fetchCompanies = async () => {
    const { payload } = await getCompanies();
    this.setState({ companies: payload });
  };

  fetchBranchesAndDepartments = async () => {
    let companyId = this.props.company?.id ?? 0
    if (this.state.lastSelectedCompany?.id) {
      companyId = this.state.lastSelectedCompany?.id
    }
    const branches = await getBranches(companyId)
    this.setState({ branches: branches.payload });
    const departments = await getDepartments(companyId)
    this.setState({ departments: departments.payload });
  };

  /**
   * CO-226 — renders the plan-ceiling refusal as an UPGRADE PROMPT, from whichever path delivered it.
   *
   * One method, called from BOTH the resolved-value branch and the `catch`, because the same refusal
   * can arrive either way and the two must not drift: the server answers with HTTP 403 (so the axios
   * interceptor rejects and this lands in `catch`), while a future 200-shaped `Result` with `errors[]`
   * would land in the resolved branch. `findApiError` reads the code off either shape.
   *
   * Switched on the CODE, never on the sentence: the sentence is user-facing prose that will be
   * reworded and translated, and matching on it would break silently the day it changes.
   *
   * Returns true when it OWNED the failure, so the caller skips the destructive banner. Returns false
   * for everything else — validation, duplicate email, a genuine server fault — which must keep the
   * red banner exactly as before.
   */
  showPlanCapIfRefused = (source: unknown): boolean => {
    const { t } = this.props;
    const failure = findApiError(source, PLAN_EMPLOYEE_CAP_REACHED, FREE_TIER_EMPLOYEE_CAP_REACHED);
    if (!failure) return false;
    // The numbers (plan name, ceiling, headcount) exist only on the server, so they arrive with the
    // refusal — structured in `details` for the localized sentence, and pre-composed in English in
    // `description` as the fallback for a backend that predates the structured payload.
    const details = failure.details;
    this.setState({
      dialogVisible: false,
      errorMessage: '',
      planCapMessage:
        (typeof failure.description === 'string' && failure.description) ||
        t('This plan does not include any more employees'),
      planCapDetails:
        details && typeof details === 'object' && !Array.isArray(details)
          ? (details as PlanCapDetails)
          : null,
    });
    return true;
  };

  submit = async () => {
    const { t } = this.props;
    if (this.state.isSubmitting) return;
    // The employee is already created and a seat already consumed — resubmitting would create a
    // second person. The button is disabled in this state too; this is the belt-and-braces half.
    if (this.state.partialSuccess) return;
    // Tracks whether `addUsers` has COMMITTED. Everything after that point is a partial success,
    // never a failure, no matter how it fails — including an exception thrown from startOnboarding.
    // `addUsers` returns Result<string[]> — the payload elements are string GUIDs, not numbers
    // (see api/General/account.ts and how the same value is passed to startOnboarding({ userId })).
    let createdUserId: string | null = null;
    try {
      if (typeof window !== 'undefined' && window.scrollTo) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      const items = [this.state.item];
      const filteredItems = items.filter(item => !(item.countryId && item.email && item.lastName && item.role && item.timeZone && item.gender));
      if (filteredItems.length > 0) {
        this.setState({ dialogVisible: true, errorMessage: t('Please fill all required fields') });
        return;
      }

      this.setState({ isSubmitting: true, dialogVisible: false, errorMessage: '', planCapMessage: '', planCapDetails: null });
      const users = await addUsers(items);
      if (users && (!users.errors || users.errors.length === 0)) {
        if (users.payload && users.payload.length > 0) {
          createdUserId = users.payload[0];
        }
        if (this.state.selectedOnboardingTemplateId && users.payload && users.payload.length > 0) {
          const newUserId = users.payload[0];
          const onboardingResult = await startOnboarding({
            userId: newUserId,
            templateId: parseInt(this.state.selectedOnboardingTemplateId, 10),
            startDate: this.state.onboardingStartDate,
            managerId: this.state.onboardingManagerId,
          });
          if (onboardingResult.errors && onboardingResult.errors.length > 0) {
            // The employee EXISTS at this point. Report the truth, not "failed to add users".
            this.setState({
              dialogVisible: false,
              errorMessage: '',
              partialSuccess: true,
              // `.description`, NOT `.message` — this codebase's Error model (models/general.ts:1528)
              // has no `message`, so `.message` is undefined at runtime and would silently collapse
              // every server-supplied reason into the generic fallback below.
              onboardingErrorMessage: onboardingResult.errors[0]?.description || t('Failed to start onboarding'),
            });
            return;
          }
        }
        this.setState({ success: true });
      } else if (users?.errors && users.errors.length > 0) {
        const failure = users.errors[0];
        if (this.showPlanCapIfRefused(users)) return;
        this.setState({ dialogVisible: true, errorMessage: failure?.description || t('Failed to add user') });
      }
    } catch (error: any) {
      console.log("error: ", error);
      const message = t(error?.errors?.[0]?.description || 'An unexpected error occurred');
      if (createdUserId != null) {
        // Thrown AFTER the create committed (network drop on startOnboarding, for example).
        // Same rule: the employee exists, so this is a partial success, never "failed to add".
        this.setState({
          dialogVisible: false,
          errorMessage: '',
          partialSuccess: true,
          onboardingErrorMessage: message,
        });
      } else {
        // CO-226 — the SAME refusal, arriving down the other path. The server answers the plan
        // ceiling with HTTP 403, and the axios interceptor rejects every non-2xx, so `addUsers`
        // THROWS instead of resolving: this catch, not the branch above, is where the refusal
        // actually lands in production. Reading only `errors[0].description` here is what made the
        // whole customer-facing half of CO-226 unreachable — the upgrade notice could never render
        // and the operator always saw the red "Failed to add users." banner.
        if (this.showPlanCapIfRefused(error)) return;
        this.setState({ dialogVisible: true, errorMessage: message });
      }
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  render() {
    const { t } = this.props;
    if (this.state.success == true) {
      return <Navigate to={`/core-hr/directory`} />
    }
    const missing = this.getMissingRequired();
    const isMissing = (name: string) => missing.includes(name);
    return (
      <>
        {/*
          CO-226 — the plan-ceiling refusal, in its own affordance. It sits above the form for the
          same reason RestrictedWriteNotice does: the operator is about to fill in a form that
          cannot be submitted, and learning that at the bottom, after typing, is the defect.
        */}
        {this.state.planCapMessage && (
          <div className="w-full mb-4">
            <PlanCapUpgradeNotice
              reason={this.state.planCapMessage}
              details={this.state.planCapDetails}
            />
          </div>
        )}
        {this.state.dialogVisible && (
          <div className="w-full mb-4">
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive flex items-center justify-between">
              <span>{t("Failed to add users. ") + this.state.errorMessage}</span>
              <button onClick={() => this.setState({ dialogVisible: false })} className="ms-2 hover:opacity-80">&times;</button>
            </div>
          </div>
        )}
        {/*
          PARTIAL SUCCESS — the employee EXISTS, only the onboarding journey did not start.
          Deliberately NOT the destructive banner above: this is not a failure, and telling the
          operator it failed is what made them resubmit and create a duplicate employee (and a
          second billable seat). No dismiss control and no retry — the only exits are the
          directory (to see the person who was in fact created) and the onboarding screen.
        */}
        {this.state.partialSuccess && (
          <div className="w-full mb-4">
            <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-4 text-sm text-foreground">
              <p className="font-semibold">{t("Employee created — onboarding not started")}</p>
              <p className="mt-1 text-muted-foreground">
                {t("The employee record was created successfully, but the onboarding journey could not be started. Do not submit this form again — that would create a duplicate employee. Open the employee from the directory and start onboarding from there.")}
              </p>
              {this.state.onboardingErrorMessage && (
                <p className="mt-1 text-muted-foreground">
                  {/* Key stays colon-free: i18n.js sets nsSeparator to ':', so t("Reason:")
                      would resolve as namespace "Reason" + empty key and render NOTHING (CO-62).
                      The colon is literal JSX, outside t(). */}
                  {t("Reason")}: {this.state.onboardingErrorMessage}
                </p>
              )}
              <Link
                to="/core-hr/directory"
                className="mt-3 inline-flex items-center text-sm font-medium text-primary hover:underline"
              >
                {t("Go to the employee directory")}
              </Link>
            </div>
          </div>
        )}
        <div className="flex flex-col items-center gap-6 p-4">
          <div className="w-full flex flex-col gap-4">
            <RestrictedWriteNotice />
            <div className="flex flex-col gap-1 mb-2">
              <h1 className="heading-2">
                {t("New Employee")}
              </h1>
              <p className="text-sm text-muted">
                {t("Fill in the required details below. Fields marked with * are mandatory.")}
              </p>
            </div>
            <hr className="border-border" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div>
                <div className="section h-full">
                  <h3 className="text-sm text-primary mb-2 font-semibold">{t('Basic Information')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Input label={t("First Name")} value={this.state.item.firstName} required error={isMissing('firstName') ? t('Required') : undefined} onChange={(ev: any) => { const item = { ...this.state.item, firstName: (ev.target as HTMLInputElement).value }; this.setState({ item }); }} />
                    </div>
                    <div>
                      <Input label={t("Last Name")} value={this.state.item.lastName} required error={isMissing('lastName') ? t('Required') : undefined} onChange={(ev: any) => { const item = { ...this.state.item, lastName: (ev.target as HTMLInputElement).value }; this.setState({ item }); }} />
                    </div>
                    <div>
                      <Select
                        label={t("Gender")}
                        required
                        error={isMissing('gender') ? t('Required') : undefined}
                        options={Object.values(Gender).filter(v => isNaN(Number(v))).map((key: any) => ({ label: t(key), value: (Gender as any)[key] }))}
                        value={this.state.item.gender}
                        onChange={(value: any) => { const item = { ...this.state.item, gender: value as Gender }; this.setState({ item }); }}
                      />
                    </div>
                    <div>
                      <CustomDatePicker label={t("Birth Date")} value={this.state.item.birthDate ? this.state.item.birthDate : null} onChange={(date: any) => { const item = { ...this.state.item, birthDate: date ?? undefined }; this.setState({ item }); }} fullWidth={true} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact & Location */}
              <div>
                <div className="section h-full">
                  <h3 className="text-sm text-primary mb-2 font-semibold">{t('Contact & Location')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Input label={t("Email")} type="email" value={this.state.item.email} required error={isMissing('email') ? t('Required') : undefined} onChange={(ev: any) => { const item = { ...this.state.item, email: (ev.target as HTMLInputElement).value }; this.setState({ item }) }} />
                    </div>
                    <div>
                      <div className={`phone-field ${isMissing('phoneNumber') ? 'is-error' : ''}`}>
                        <label className="text-sm font-semibold mb-0.5 block">{t("Phone Number")}</label>
                        <PhoneInput title={t("Phone Number")} international className="w-full" value={this.state.item.phoneNumber} onChange={(ev: any) => { const item = { ...this.state.item, phoneNumber: ev }; this.setState({ item }) }} />
                      </div>
                    </div>
                    <div>
                      <Select
                        label={t("Country")}
                        required
                        searchable
                        error={isMissing('countryId') ? t('Required') : undefined}
                        options={this.state.countries.map(x => ({ label: x.name, value: x.id }))}
                        value={this.state.item.countryId || ''}
                        onChange={(value: any) => {
                          const selectedId = value;
                          const selectedCountry = this.state.countries.find(x => x.id === selectedId);
                          const item = { ...this.state.item, country: selectedCountry, countryId: parseInt(String(selectedId)) };
                          if (selectedCountry && (!item.phoneNumber || item.phoneNumber.length < 6)) {
                            item.phoneNumber = '+' + selectedCountry.phoneCode;
                          }
                          // Auto-populate the timezone when the user hasn't picked one yet.
                          // Multi-zone countries (US, RU, CA, AU, BR, …) are intentionally
                          // skipped — the helper returns null and the user picks manually.
                          if (selectedCountry && !item.timeZone) {
                            const tz = pickTimezoneForCountry(selectedCountry.iso, this.state.timeZones);
                            if (tz) item.timeZone = tz;
                          }
                          this.setState({ item });
                        }}
                      />
                    </div>
                    <div>
                      <Select
                        label={t("Timezone")}
                        required
                        searchable
                        error={isMissing('timeZone') ? t('Required') : undefined}
                        options={this.state.timeZones.map(x => ({ label: x.displayName, value: x.id }))}
                        value={this.state.item.timeZone || ''}
                        onChange={(value: any) => { const item = { ...this.state.item, timeZone: value }; this.setState({ item }); }}
                      />
                    </div>
                    <div className="md:col-span-2 pt-1">
                      <Checkbox
                        checked={this.state.item.sendWelcomeEmail ?? true}
                        onChange={(ev: React.ChangeEvent<HTMLInputElement>) => { const item = { ...this.state.item, sendWelcomeEmail: ev.target.checked }; this.setState({ item }); }}
                        label={t("Send welcome email")}
                        description={t("The new employee will receive an email with their sign-in details.")}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Employment Details */}
              <div>
                <div className="section h-full">
                  <h3 className="text-sm text-primary mb-2 font-semibold">{t('Employment Details')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Input label={t("Employee Id")} value={this.state.item.employeeId} onChange={(ev: any) => { const item = { ...this.state.item, employeeId: (ev.target as HTMLInputElement).value }; this.setState({ item }) }} />
                    </div>
                    <div>
                      <Input label={t("Title")} value={this.state.item.title} onChange={(ev: any) => { const item = { ...this.state.item, title: (ev.target as HTMLInputElement).value }; this.setState({ item }) }} />
                    </div>
                    <div>
                      <Select
                        label={t("Role")}
                        required
                        error={isMissing('role') ? t('Required') : undefined}
                        options={this.state.roles.map(x => ({ label: x, value: x }))}
                        value={this.state.item.role}
                        onChange={(value: any) => { const item = { ...this.state.item, role: value }; this.setState({ item }) }}
                      />
                    </div>
                    <div>
                      <Select
                        label={t("Branch")}
                        options={this.state.branches.map(x => ({ label: x.name, value: x.id }))}
                        value={this.state.item.branchId || ''}
                        onChange={(value: any) => { const item = { ...this.state.item, branchId: parseInt(String(value)) }; this.setState({ item }); }}
                      />
                    </div>
                    <div>
                      <Select
                        label={t("Department")}
                        options={this.state.departments.map(x => ({ label: x.name, value: x.id }))}
                        value={this.state.item.departmentId || ''}
                        onChange={(value: any) => { const item = { ...this.state.item, departmentId: parseInt(String(value)) }; this.setState({ item }) }}
                      />
                    </div>
                    <div>
                      <Select
                        label={t("Employment Type")}
                        options={[
                          { label: t("Full Time"), value: "FullTime" },
                          { label: t("Part Time"), value: "PartTime" },
                          { label: t("Contract"), value: "Contract" },
                          { label: t("Executive"), value: "Executive" }
                        ]}
                        value={this.state.item.employmentType || ''}
                        onChange={(value: any) => { const item = { ...this.state.item, employmentType: value as string }; this.setState({ item }); }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Social */}
              <div>
                <div className="section h-full">
                  <h3 className="text-sm text-primary mb-2 font-semibold">{t('Social')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Input label={t("LinkedIn Url")} value={this.state.item.linkedInUrl} onChange={(ev: any) => { const item = { ...this.state.item, linkedInUrl: (ev.target as HTMLInputElement).value }; this.setState({ item }); }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Onboarding Setup */}
            <div className="lg:col-span-2 mt-4">
              <div className="section">
                <h3 className="text-sm text-primary mb-1 font-semibold">{t('Onboarding Setup')}</h3>
                <p className="text-xs text-muted-foreground mb-3">{t('Optionally assign an onboarding process when creating this employee.')}</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Select
                      label={t('Onboarding Template')}
                      options={[
                        { label: t('No onboarding process'), value: '' },
                        ...this.state.onboardingTemplates.map(x => ({
                          label: x.name + (x.isDefault ? ` (${t('Default')})` : ''),
                          value: String(x.id)
                        }))
                      ]}
                      value={this.state.selectedOnboardingTemplateId}
                      onChange={(value: any) => this.setState({ selectedOnboardingTemplateId: value })}
                    />
                  </div>
                  {this.state.selectedOnboardingTemplateId && (
                    <>
                      <div>
                        <DateField
                          label={t('Start Date')}
                          required
                          error={isMissing('onboardingStartDate') ? t('Required') : undefined}
                          value={this.state.onboardingStartDate}
                          onChange={(v) => this.setState({ onboardingStartDate: v })}
                          data-testid="onboarding-start-date"
                        />
                      </div>
                      <div>
                        <Select
                          label={t('Reporting Manager')}
                          required
                          error={isMissing('onboardingManagerId') ? t('Required') : undefined}
                          options={[
                            { label: t('Select a manager...'), value: '' },
                            ...this.state.managers.map(x => ({ label: x.fullName, value: x.id }))
                          ]}
                          value={this.state.onboardingManagerId}
                          onChange={(value: any) => this.setState({ onboardingManagerId: value })}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <hr className="border-border mt-2" />
            <div className="flex justify-end mt-2">
              {/* CO-24 repair — `title` on the Button itself is INERT (disabled controls get
                  `pointer-events-none`). The reason lives in <RestrictedWriteNotice /> at the top of
                  this form and is linked by aria-describedby; the span keeps only the
                  missing-fields hint, which applies to an otherwise-enabled form. */}
              {/* Once the employee has been created, Submit is retired: pressing it again would
                  create a SECOND employee and consume a second billable seat. The amber banner
                  above carries the only onward action. */}
              <span
                title={
                  this.state.partialSuccess
                    ? t('This employee has already been created')
                    : missing.length
                      ? t('Please complete required fields')
                      : undefined
                }
              >
                <Button
                  variant="default"
                  {...(this.props.writeAccess.blocked ? { 'aria-disabled': true, 'aria-describedby': RESTRICTED_WRITE_REASON_ID } : {})}
                  disabled={missing.length > 0 || this.state.isSubmitting || this.state.partialSuccess || this.props.writeAccess.blocked}
                  loading={this.state.isSubmitting}
                  onClick={this.submit}
                  data-testid="add-user-submit-btn"
                >
                  {this.state.isSubmitting ? t("Creating...") : t("Submit")}
                </Button>
              </span>
            </div>
          </div>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state: any) => {
  return {
    rtl: state.user.rtl,
    userId: state.user.userId,
    company: state.user.company,
    companyPrimaryColor: state.user.companyPrimaryColor,
    companyName: state.user.companyName,
    role: state.user.role
  }
}
export default withWriteAccess(connect(mapStateToProps)(withTranslation()(AddUser as any) as any) as any);
